import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Trash2,
  Eye,
  Calendar,
  User,
  Building,
  FileText,
  XCircle,
  PanelTopOpen,
  MoreVertical
} from "lucide-react";
import { complianceService, userService, plantService } from '../../services/api';
import {
  getStatusBadge,
  formatDate,
  extractArrayData,
} from '../../utils/helpers';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import LoadingSpinner from '../Common/LoadingSpinner';
import ImportModal from '../Import_Export/ImportModal';
import ConfirmModal from '../Common/ConfirmModal';






const loadJsPDFScripts = () => {
  return new Promise((resolve, reject) => {
    // Load jsPDF UMD
    const jspdfScript = document.createElement("script");
    jspdfScript.src = "https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js";
    jspdfScript.async = true;
    jspdfScript.onload = () => {
      // Load jspdf-autotable after jsPDF
      const autotableScript = document.createElement("script");
      autotableScript.src = "https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js";
      autotableScript.async = true;
      autotableScript.onload = () => resolve();
      autotableScript.onerror = () => reject(new Error("Failed to load jspdf-autotable"));
      document.head.appendChild(autotableScript);
    };
    jspdfScript.onerror = () => reject(new Error("Failed to load jsPDF"));
    document.head.appendChild(jspdfScript);
  });
};

// Custom colors for statuses
const statusColors = {
  pending: '#FF6B6B', // Red
  'in-progress': '#4ECDC4', // Teal
  completed: '#45B7D1', // Blue
  overdue: '#FFA07A', // Orange 
};

// Function to prepare data for pie chart
const getPieChartData = (compliances) => {
  const statusCounts = {
    pending: 0,
    'in-progress': 0,
    completed: 0,
    overdue: 0,
  };

  compliances.forEach((compliance) => {
    if (statusCounts.hasOwnProperty(compliance.status)) {
      statusCounts[compliance.status]++;
    }
  });

  return Object.entries(statusCounts)
    .map(([status, count]) => ({
      id: status,
      label: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: statusColors[status],
    }))
    .filter((item) => item.value > 0);
};

// Function to prepare data for bar chart
const getBarChartData = (compliances) => {
  const statusCounts = {
    pending: 0,
    'in-progress': 0,
    completed: 0,
    overdue: 0,
  };

  compliances.forEach((compliance) => {
    if (statusCounts.hasOwnProperty(compliance.status)) {
      statusCounts[compliance.status]++;
    }
  });

  return Object.keys(statusCounts)
    .map((status) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1),
      count: statusCounts[status],
      [status]: statusCounts[status], // For color mapping
    }))
    .filter((item) => item.count > 0);
};

// Function to prepare data for line chart
const getLineChartData = (compliances) => {
  const statusCountsByDate = {};
  const statuses = ['pending', 'in-progress', 'overdue'];

  compliances.forEach((compliance) => {
    const date = compliance.createdAt ? new Date(compliance.createdAt).toISOString().split('T')[0] : null;
    if (date && !statusCountsByDate[date]) {
      statusCountsByDate[date] = { date, pending: 0, 'in-progress': 0, /* overdue: 0, */ };
    }
    if (date && statuses.includes(compliance.status)) {
      statusCountsByDate[date][compliance.status]++;
    }
  });

  return statuses
    .map((status) => ({
      id: status.charAt(0).toUpperCase() + status.slice(1),
      color: statusColors[status],
      data: Object.values(statusCountsByDate).map((entry) => ({
        x: entry.date,
        y: entry[status],
      })),
    }))
    .filter((series) => series.data.some((point) => point.y > 0));
};

const Performance = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State for filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    frequency: '',
    assignedTo: '',
    plant: '',
    dueTill: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showCompliance, setShowCompliace] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [complianceToDelete, setComplianceToDelete] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);


  const ChangeshowCompliance = () => {
    setShowCompliace(!showCompliance)
  }

  // Fetch all filtered compliances (non-paginated) for charts and table
  // Filter by status on the frontend for responsiveness and chart distribution
  const {
    data: compliancesData,
    isLoading: isCompliancesLoading,
    error: compliancesError,
  } = useQuery(
    ['compliances-performance', { ...filters, status: undefined }],
    () => {
      const { status, ...backendFilters } = filters;
      return complianceService.exportCompliances(backendFilters);
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Fetch users for filter dropdown
  const { data: usersData } = useQuery(
    ['users'],
    () => userService.getUsers(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Fetch plants for filter dropdown
  const { data: plantsData } = useQuery(
    ['plants'],
    () => plantService.getPlants(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const compliances = extractArrayData(compliancesData) || [];
  const users = extractArrayData(usersData) || [];
  const plants = extractArrayData(plantsData) || [];

  // Filter by status on the frontend as requested
  const filteredByStatus = (compliances || []).filter((compliance) => {
    if (!filters.status) return true;
    return compliance.status?.toLowerCase() === filters.status.toLowerCase();
  });

  const filteredCompliances = filteredByStatus;

  // Log filter application for debugging
  console.log('Filters:', filters);
  console.log('Raw Compliances:', compliances);
  console.log('Filtered Compliances:', filteredCompliances);

  // Paginate filtered compliances
  const totalFiltered = filteredCompliances.length;
  const totalPages = Math.ceil(totalFiltered / pageSize);
  const paginatedCompliances = filteredCompliances.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Prepare data for charts
  const pieChartData = getPieChartData(filteredCompliances);
  const barChartData = getBarChartData(filteredCompliances);
  const lineChartData = getLineChartData(filteredCompliances);

  // Delete compliance mutation
  const deleteMutation = useMutation(
    (id) => complianceService.deleteCompliance(id),
    {
      onSuccess: () => {
        toast.success('Compliance deleted successfully');
        queryClient.invalidateQueries('compliances');
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || 'Failed to delete compliance'
        );
      },
    }
  );

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      frequency: '',
      assignedTo: '',
      plant: '',
      dueTill: '',
    });
    setCurrentPage(1);
  };

  // Handle delete
  const handleDelete = (id) => {
    setComplianceToDelete(id);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (complianceToDelete) {
      await deleteMutation.mutateAsync(complianceToDelete);
      setShowConfirmDelete(false);
      setComplianceToDelete(null);
    }
  };

  // Handle import
  const handleImport = () => {
    setShowImportModal(true);
  };

  // Handle export
  const handleExport = async () => {
    try {
      // Load jsPDF and autotable scripts dynamically
      await loadJsPDFScripts();

      // Access jsPDF from the global window object (UMD exposes it as window.jsPDF)
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Compliance Report", 14, 20);

      const columns = [
        { header: "Sr. No", dataKey: "srNo" },
        { header: "Title", dataKey: "title" },
        { header: "Status", dataKey: "status" },
        { header: "Due Date", dataKey: "dueDate" },
        { header: "Assigned To", dataKey: "assignedTo" },
        { header: "Plant", dataKey: "plant" },
        { header: "Frequency", dataKey: "frequency" },
      ];

      const data = filteredCompliances.map((compliance, index) => ({
        srNo: index + 1,
        title: compliance.title || "N/A",
        status: compliance.status || "N/A",
        dueDate: formatDate(compliance.dueDate) || "N/A",
        assignedTo:
          Array.isArray(compliance.assignedTo) && compliance.assignedTo.length > 0
            ? compliance.assignedTo.map((user) => user.name || "Unknown").join(", ")
            : "Unassigned",
        plant: compliance.plant?.name || "N/A",
        frequency: compliance.frequency || "N/A",
      }));

      doc.autoTable({
        columns,
        body: data,
        startY: 30,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 30 },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(
          `Generated on ${new Date().toLocaleString()}`,
          14,
          doc.internal.pageSize.height - 10
        );
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width - 30,
          doc.internal.pageSize.height - 10
        );
      }

      doc.save("compliance_report.pdf");
      toast.success("Compliance report exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export compliances as PDF");
    }
  };

  // Handle import submission
  const handleImportSubmit = async (file) => {
    const toastId = toast.loading('Importing compliances...');
    setIsImporting(true);
    try {
      await complianceService.importCompliances(file);
      toast.success('Compliances imported successfully', { id: toastId });
      queryClient.invalidateQueries('compliances-performance');
      setShowImportModal(false);
    } catch (error) {
      console.error('Import error:', error);
      const backendErrors = error.response?.data?.errors;
      const errorMessage = error.response?.data?.message || 'Failed to import compliances';

      if (Array.isArray(backendErrors) && backendErrors.length > 0) {
        // Show first few errors if many
        const displayErrors = backendErrors.slice(0, 3).map(err => {
          if (typeof err === 'string') return err;
          return `Row ${err.row}: ${err.message || err.error}`;
        }).join('\n');

        toast.error(`${errorMessage}:\n${displayErrors}${backendErrors.length > 3 ? '\n...and more' : ''}`, {
          id: toastId,
          duration: 5000,
          icon: null
        });
      } else {
        toast.error(errorMessage, {
          id: toastId,
          duration: 5000,
          icon: null
        });
      }
    } finally {
      setIsImporting(false);
    }
  };

  if (compliancesError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Error loading compliances
          </h3>
          <p className="mt-1 text-sm text-gray-500">{compliancesError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-600">Compliance Status Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">Visualize compliance status metrics</p>
        </div>
      </div>

      {/* Import Modal */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSubmit={handleImportSubmit}
        isLoading={isImporting}
      />

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search compliances..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>
            </div>
          </form>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select
                  value={filters.frequency}
                  onChange={(e) => handleFilterChange('frequency', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Frequencies</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <select
                  value={filters.assignedTo}
                  onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Users</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plant</label>
                <select
                  value={filters.plant}
                  onChange={(e) => handleFilterChange('plant', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Plants</option>
                  {plants.map((plant) => (
                    <option key={plant._id} value={plant._id}>
                      {plant.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Till</label>
                <input
                  type="date"
                  value={filters.dueTill}
                  onChange={(e) => handleFilterChange('dueTill', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {showFilters && (
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>

      {isCompliancesLoading ? (
        <div className="p-6">
          <LoadingSpinner text="Loading compliances..." />
        </div>
      ) : (
        <div className="space-y-6">
          {/* First Row: Pie and Bar Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
              <h2 className="text-lg sm:text-xl font-semibold mb-4 text-gray-700">Status Distribution (Pie)</h2>
              {pieChartData.length === 0 ? (
                <p className="text-gray-500 text-center">No data available for the pie chart.</p>
              ) : (
                <div className="h-80 sm:h-96">
                  <ResponsivePie
                    data={pieChartData}
                    margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={8}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsTextColor="#333333"
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{ from: 'color' }}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                    colors={pieChartData.map((item) => item.color)}
                    legends={[
                      {
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: 15,
                        translateY: 56,
                        itemsSpacing: 0,
                        itemWidth: 100,
                        itemHeight: 18,
                        itemTextColor: '#999',
                        itemDirection: 'left-to-right',
                        itemOpacity: 1,
                        symbolSize: 18,
                        symbolShape: 'circle',
                        effects: [{ on: 'hover', style: { itemTextColor: '#000' } }],
                      },
                    ]}
                  />
                </div>
              )}
            </div>

            {/* Bar Chart */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
              <h2 className="text-lg sm:text-xl font-semibold mb-4 text-gray-700">Status Distribution (Bar)</h2>
              {barChartData.length === 0 ? (
                <p className="text-gray-500 text-center">No data available for the bar chart.</p>
              ) : (
                <div className="h-80 sm:h-96">
                  <ResponsiveBar
                    data={barChartData}
                    keys={['pending', 'in-progress', 'completed', 'overdue']}
                    indexBy="status"
                    margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                    padding={0.3}
                    valueScale={{ type: 'linear' }}
                    indexScale={{ type: 'band', round: true }}
                    colors={barChartData.map((item) => statusColors[item.status.toLowerCase()])}
                    borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,

                      legendPosition: 'middle',
                      legendOffset: 32,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Count',
                      legendPosition: 'middle',
                      legendOffset: -50,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    legends={[
                      {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 120,
                        translateY: 0,
                        itemsSpacing: 2,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [{ on: 'hover', style: { itemOpacity: 1 } }],
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Second Row: Line Chart */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <h2 className="text-lg sm:text-xl font-semibold mb-4 text-gray-700">Status Trend Over Time</h2>
            {lineChartData.length === 0 ? (
              <p className="text-gray-500 text-center">No data available for the line chart.</p>
            ) : (
              <div className="h-80 sm:h-96">
                <ResponsiveLine
                  data={lineChartData}
                  margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                  xScale={{ type: 'point' }}
                  yScale={{
                    type: 'linear',
                    min: 'auto',
                    max: 'auto',
                    stacked: false,
                    reverse: false,
                  }}
                  yFormat=" >-.2f"
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 45,
                    legend: 'Date',
                    legendPosition: 'middle',
                    legendOffset: 36,
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Count',
                    legendPosition: 'middle',
                    legendOffset: -40,
                  }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  colors={lineChartData.map((item) => item.color)}
                  legends={[
                    {
                      anchor: 'bottom-right',
                      direction: 'column',
                      justify: false,
                      translateX: 100,
                      translateY: 0,
                      itemsSpacing: 0,
                      itemDirection: 'left-to-right',
                      itemWidth: 80,
                      itemHeight: 20,
                      itemOpacity: 0.75,
                      symbolSize: 12,
                      symbolShape: 'circle',
                      symbolBorderColor: 'rgba(0, 0, 0, .5)',
                      effects: [{ on: 'hover', style: { itemBackground: 'rgba(0, 0, 0, .03)', itemOpacity: 1 } }],
                    },
                  ]}
                />
              </div>
            )}
          </div>

          {/* Compliance List Table */}
          <div className="bg-white shadow rounded-lg">

            <div className="p-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-semibold mb-4 text-gray-700">
                  Filtered Compliances
                </h2>
                <button onClick={ChangeshowCompliance} className=" mb-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                > <PanelTopOpen className="h-4 w-4 mr-2" /> Show</button>
              </div>


              {
                showCompliance && <div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sr. No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Due Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Assigned To
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Plant
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Frequency
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedCompliances.length > 0 ? (
                          paginatedCompliances.map((compliance, index) => (
                            <tr key={compliance._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                {(currentPage - 1) * pageSize + index + 1}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <FileText className="h-5 w-5 text-gray-400 mr-3" />
                                  <div>
                                    <div className="relative group max-w-[150px]">
                                      <div className="truncate text-sm font-medium text-gray-900">
                                        {compliance.title || 'N/A'}
                                      </div>
                                      <div className="absolute left-0 top-full mt-2 hidden group-hover:flex z-50 w-auto bg-gray-900 text-white text-xs rounded px-2 py-1 shadow-lg whitespace-nowrap">
                                        {compliance.title || 'N/A'}
                                      </div>
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {compliance.license || 'No description'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(compliance.status)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                  <span className="text-sm text-gray-900">
                                    {formatDate(compliance.dueDate) || 'N/A'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <User className="h-4 w-4 text-gray-400 mr-2" />
                                  <span className="text-sm text-gray-900">
                                    {Array.isArray(compliance.assignedTo) &&
                                      compliance.assignedTo.length > 0
                                      ? compliance.assignedTo
                                        .map((user) => user.name || 'Unknown')
                                        .join(', ')
                                      : 'Unassigned'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Building className="h-4 w-4 text-gray-400 mr-2" />
                                  <span className="text-sm text-gray-900">
                                    {compliance.plant?.name || 'N/A'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {compliance.frequency || 'N/A'}
                                </span>
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white ${openDropdown === compliance._id ? 'z-20' : 'z-10'}`}>
                                <div className="relative">
                                  <button
                                    className="flex items-center justify-center p-1 rounded hover:          "
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenDropdown(openDropdown === compliance._id ? null : compliance._id);
                                    }}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </button>

                                  {openDropdown === compliance._id && (
                                    <div className="absolute right-0 mt-2  w-48  bg-white rounded-md  z-50 border border-gray-200  text-gray-600 ">   <div className="py-1">
                                      <Link
                                        to={`/compliances/${compliance._id}`}
                                        className="block rounded px-2 py-2 "
                                      >
                                        <div className="flex items-center hover:bg-blue-100 rounded px-2 py-1">
                                          <Eye className="h-4 w-4 mr-2" />
                                          View
                                        </div>
                                      </Link>

                                      {(user?.role === "admin" ||
                                        (user?.role === "employee" &&
                                          compliance.assignedTo?.some((u) => u._id === user._id))) && (
                                          <Link
                                            to={`/compliances/${compliance._id}/edit`}
                                            className="block rounded px-2 py-2"
                                          >
                                            <div className="flex items-center text-gray-600  hover:bg-blue-100 rounded px-2 py-1">
                                              <Edit className="h-4 w-4 mr-2" />
                                              Edit
                                            </div>
                                          </Link>
                                        )}


                                      {user?.role === "admin" && (
                                        <>
                                          <button
                                            onClick={() => handleDelete(compliance._id)}
                                            className="w-full text-left block rounded px-2 py-2  text-gray-600 "
                                          >
                                            <div className="flex items-center hover:bg-blue-100 rounded px-2 py-1">
                                              <Trash2 className="h-4 w-4 mr-2" />
                                              Delete
                                            </div>
                                          </button>



                                        </>
                                      )}
                                    </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="8"
                              className="px-6 py-4 text-center text-gray-500"
                            >
                              No compliances found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalFiltered > 0 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <button
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                            <span className="font-medium">{Math.min(currentPage * pageSize, totalFiltered)}</span> of{' '}
                            <span className="font-medium">{totalFiltered}</span> results
                          </p>
                        </div>
                        <div>
                          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                            <button
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Previous
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(
                                (page) =>
                                  page === 1 ||
                                  page === totalPages ||
                                  Math.abs(page - currentPage) <= 1
                              )
                              .map((page, index, array) => (
                                <React.Fragment key={page}>
                                  {index > 0 && array[index - 1] !== page - 1 && (
                                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                      ...
                                    </span>
                                  )}
                                  <button
                                    onClick={() => setCurrentPage(page)}
                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                      }`}
                                  >
                                    {page}
                                  </button>
                                </React.Fragment>
                              ))}
                            <button
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage >= totalPages}
                              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Next
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              }
            </div>
          </div>

        </div>
      )}
      <ConfirmModal
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        title="Delete Compliance"
        message="Are you sure you want to delete this compliance? This action cannot be undone."
        confirmText="Delete"
      />
    </div>
  );
};

export default Performance;
